import { nanoid } from 'nanoid';

import i18n from '@sola/locales/index.ts';
import { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import {
  LocalAlertConfig,
  LocalAlertCreateProps,
} from '../types/localeAlerts.ts';

/**
 * Function for creating local alert data with an id.
 */
export const createLocalAlert = (
  alertProps: LocalAlertCreateProps,
): LocalAlertConfig => {
  return { id: nanoid(), ...alertProps };
};

export const createPaymentMethodSuccessAlert = (
  { creditCard, ach }: GraphQLSupporterPaymentMethod,
  actionTranslationKey: string,
): LocalAlertCreateProps => {
  const { t } = i18n;
  let lastFour = creditCard?.lastFour || '';
  let type = creditCard?.cardType || '';

  if (ach) {
    lastFour = ach.lastFourOfAccountNumber || '';
    type = t('my:payments.methodTypes.ach.short');
  }

  return {
    translationKey: 'payments.success',
    variant: 'positive',
    translationData: {
      lastFour,
      type,
      action: t(`my:${actionTranslationKey}`),
    },
  };
};

export const createPaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    const { t } = i18n;
    return {
      translationKey: 'payments.partialSuccess',
      variant: 'warning',
      dismissible: false,
      action: (
        <a href={t('my:ctas.contactSupport.url')}>
          {t('my:ctas.contactSupport.text')}
        </a>
      ),
    };
  };

export const removePaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    const { t } = i18n;

    return {
      translationKey: 'payments.remove.partialSuccess',
      variant: 'negative',
      dismissible: true,
      action: i18n.exists('my:ctas.contactSupport.text') ? (
        <a href={t('my:ctas.contactSupport.url')}>
          {t('my:ctas.contactSupport.text')}
        </a>
      ) : undefined,
    };
  };

export const removePaymentMethodFailureAlert = (): LocalAlertCreateProps => {
  const { t } = i18n;

  return {
    translationKey: 'payments.remove.failure',
    variant: 'negative',
    dismissible: true,
    action: i18n.exists('my:ctas.contactSupport.text') ? (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ) : undefined,
  };
};

export const createPaymentPlanSuccessAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'commitments.detailsSheet.paymentPlanSuccess',
    variant: 'positive',
    closable: true,
  };
};

export const createSupportAlert = (translationKey: string) => {
  const { t } = i18n;

  return createLocalAlert({
    translationKey,
    variant: 'negative',
    dismissible: true,
    action: i18n.exists('my:ctas.contactSupport.text') ? (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ) : undefined,
  });
};

export const createCommitmentPaymentAddErrorAlert =
  (): LocalAlertCreateProps => {
    const { t } = i18n;

    return {
      translationKey: 'makeAPayment.error',
      variant: 'negative',
      dismissible: true,
      action: (
        <a href={t('my:ctas.contactSupport.url')}>
          {t('my:ctas.contactSupport.text')}
        </a>
      ),
    };
  };

export const createPartialDataAlert = (
  dataKey: string,
): LocalAlertCreateProps => {
  const { t } = i18n;
  return {
    translationKey: `errors.partialData.${dataKey}`,
    variant: 'warning',
    action: (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ),
  };
};

export const createPartialDataAlertMulti = (): LocalAlertCreateProps => {
  const { t } = i18n;
  return {
    translationKey: `errors.partialData.base`,
    variant: 'warning',
    action: (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ),
  };
};

export const createAddressUpdateSuccessAlert = (): LocalAlertCreateProps => ({
  translationKey: 'my-settings:settings.addressUpdate.success',
  variant: 'positive',
  closable: true,
});

export const createAddressUpdateErrorAlert = (): LocalAlertCreateProps => {
  const { t } = i18n;
  return {
    translationKey: 'my-settings:settings.addressUpdate.error',
    variant: 'negative',
    dismissible: true,
    action: (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ),
  };
};
